import { createSlice } from "@reduxjs/toolkit";
import {
  createAssignedPlan,
  getAllAssignedPlans,
  getAssignedPlanById,
  updateAssignedPlan,
  deleteAssignedPlan,
} from "./assignPlanAction";

interface AssignPlanState {
  assignedPlans: {
    id: string;
    user: {
      id: string;
      firstname: string;
      lastname: string;
      email: string;
    };
    plan: {
      id: string;
      planName: string;
      price: number;
      duration: number;
    };
    duration: number;
    createdAt: string;
    updatedAt: string;
  }[];
  selectedAssignedPlan: {
    id: string;
    user: {
      id: string;
      firstname: string;
      lastname: string;
      email: string;
    };
    plan: {
      id: string;
      planName: string;
      price: number;
      duration: number;
    };
    duration: number;
    createdAt: string;
    updatedAt: string;
  } | null;
  loading: boolean;
  error: string | null;
}

const initialState: AssignPlanState = {
  assignedPlans: [],
  selectedAssignedPlan: null,
  loading: false,
  error: null,
};

const assignPlanSlice = createSlice({
  name: "assignPlan",
  initialState,
  reducers: {
    clearAssignPlanMessages(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // CREATE ASSIGNED PLAN
      .addCase(createAssignedPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAssignedPlan.fulfilled, (state, action) => {
        state.loading = false;
        const assignPlan = action.payload.data?.assignPlan;
        if (assignPlan) {
          state.assignedPlans.push(assignPlan);
        }
        state.error = null;
      })
      .addCase(createAssignedPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to create assigned plan";
      })

      // GET ALL ASSIGNED PLANS
      .addCase(getAllAssignedPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAssignedPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.assignedPlans = action.payload.data?.assignPlans || [];
        state.error = null;
      })
      .addCase(getAllAssignedPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve assigned plans";
      })

      // GET ASSIGNED PLAN BY ID
      .addCase(getAssignedPlanById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAssignedPlanById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedAssignedPlan = action.payload.data?.assignPlan || null;
        state.error = null;
      })
      .addCase(getAssignedPlanById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to retrieve assigned plan";
      })

      // UPDATE ASSIGNED PLAN
      .addCase(updateAssignedPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAssignedPlan.fulfilled, (state, action) => {
        state.loading = false;
        const updatedPlan = action.payload.data?.assignPlan;
        if (updatedPlan) {
          state.assignedPlans = state.assignedPlans.map((plan) =>
            plan.id === updatedPlan.id ? updatedPlan : plan
          );
          if (state.selectedAssignedPlan?.id === updatedPlan.id) {
            state.selectedAssignedPlan = updatedPlan;
          }
        }
        state.error = null;
      })
      .addCase(updateAssignedPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update assigned plan";
      })

      // DELETE ASSIGNED PLAN
      .addCase(deleteAssignedPlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAssignedPlan.fulfilled, (state, action) => {
        state.loading = false;
        const deletedId = action.meta.arg;
        state.assignedPlans = state.assignedPlans.filter(
          (plan) => plan.id !== deletedId
        );
        if (state.selectedAssignedPlan?.id === deletedId) {
          state.selectedAssignedPlan = null;
        }
        state.error = null;
      })
      .addCase(deleteAssignedPlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete assigned plan";
      });
  },
});

export const { clearAssignPlanMessages } = assignPlanSlice.actions;
export default assignPlanSlice.reducer;
